import {setAdminColorPalettes, setBrandKitColorPalettes, setFilterPills, setRecentPalettes} from '@/reducers/color-palettes.reducer';
import type {UserColorPaletteInfo} from '@Components/auto-branding/auto-branding.types';
import {canUserAccessBrandKits} from '@Libraries/brands-library';
import type {HexColors} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {isUserLoggedIn} from '@Libraries/user.library';
import type {SeasonName} from '@Libraries/seasons.library';

export interface ColorPaletteInfo {
  colors: HexColors;
  id?: string;
  type?: ColorPaletteType;
  name?: string;
  tags?: ColorPaletteTag[];
}

export enum ColorPaletteType {
  Admin = 1,
  BrandKit = 2,
  Recent = 3,
}

export interface ColorPalettePill {
  id: number;
  name: string;
  tags: ColorPaletteTag[];
  season?: SeasonName;
}

interface ColorPaletteTag {
  id: number;
  name: string;
}

export const isSameColorPalette = (a: ColorPaletteInfo | null, b: ColorPaletteInfo | null): boolean => {
  if (a === null || b === null) {
    return false;
  }

  if (a.colors.length !== b.colors.length) {
    return false;
  }

  for (let i = 0; i < a.colors.length; i++) {
    if (a.colors[i] !== b.colors[i]) {
      return false;
    }
  }

  return true;
};

export const addPaletteToRecents = async (palette: ColorPaletteInfo): Promise<void> => {
  const currentRecentPalettes: ColorPaletteInfo[] = window.PMW.redux.store.getState().colorPalettes.recentPalettes ?? [];
  const filteredPalettes = currentRecentPalettes.filter((p) => p.id !== palette.id);
  const newRecentPalettes = [palette, ...filteredPalettes];

  window.PMW.redux.store.dispatch(setRecentPalettes(newRecentPalettes));

  if (isUserLoggedIn()) {
    try {
      await window.PMW.writeLocal('colorpalettes/addPaletteToRecents', {palette});
    } catch (error) {
      console.error('Error adding palette to recents', error);
    }
  }
};

export const fetchAndUpdateRecentPalettes = async (): Promise<void> => {
  if (!isUserLoggedIn()) {
    if (window.PMW.redux.store.getState().colorPalettes.recentPalettes) {
      return;
    }

    window.PMW.redux.store.dispatch(setRecentPalettes([]));
    return;
  }

  const recentPalettes = await fetchRecentPalettesFromServer();
  window.PMW.redux.store.dispatch(setRecentPalettes(recentPalettes));
};

export const getColorPaletteFilterPills = async (): Promise<ColorPalettePill[]> => {
  const colorPalettePillsReduxState = getColorPaletteFilterPillsFromRedux();
  if (colorPalettePillsReduxState !== undefined) {
    return colorPalettePillsReduxState;
  }

  const colorPalettePills = await fetchColorPaletteFilterPillFromServer();
  window.PMW.redux.store.dispatch(setFilterPills(colorPalettePills));
  return colorPalettePills;
};

export const getAdminColorPalettes = async (): Promise<ColorPaletteInfo[]> => {
  const adminColorPalettesReduxState = getAdminColorPalettesFromRedux();
  if (adminColorPalettesReduxState !== undefined) {
    return adminColorPalettesReduxState;
  }

  const adminColorPalettes = await fetchAdminColorPalettesFromServer();

  const colorPalettes = adminColorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });

  window.PMW.redux.store.dispatch(setAdminColorPalettes(colorPalettes));

  return colorPalettes;
};

export const getUserBrandKitColorPalettes = async (): Promise<ColorPaletteInfo[]> => {
  if (!isUserLoggedIn() || !canUserAccessBrandKits()) {
    window.PMW.redux.store.dispatch(setBrandKitColorPalettes([]));

    return [];
  }

  let colorPalettes;

  const brandKitColorPalettesReduxState = getBrandKitColorPalettesFromRedux();
  if (brandKitColorPalettesReduxState) {
    colorPalettes = brandKitColorPalettesReduxState;
  } else {
    const userColorPalettes = await fetchBrandKitColorPalettesFromServer();

    colorPalettes = userColorPalettes.map((userColorPalette): ColorPaletteInfo => {
      return {
        id: userColorPalette.customizedFromId,
        colors: userColorPalette.colors,
        name: userColorPalette.name,
        type: ColorPaletteType.BrandKit,
      };
    });

    window.PMW.redux.store.dispatch(setBrandKitColorPalettes(colorPalettes));
  }

  return colorPalettes;
};

export const getAdminColorPalettesFromRedux = (): ColorPaletteInfo[] | undefined => {
  return window.PMW.redux.store.getState().colorPalettes.adminColorPalettes;
};

export const getBrandKitColorPalettesFromRedux = (): ColorPaletteInfo[] | undefined => {
  return window.PMW.redux.store.getState().colorPalettes.brandKitColorPalettes;
};

export const getColorPaletteFilterPillsFromRedux = (): ColorPalettePill[] | undefined => {
  return window.PMW.redux.store.getState().colorPalettes.filterPills;
};

const fetchAdminColorPalettesFromServer = async (): Promise<ColorPaletteInfo[]> => {
  try {
    return (await window.PMW.readLocal('colorpalettes/getAdminColorPalettes', {})) as ColorPaletteInfo[];
  } catch (error) {
    console.error('Error fetching color palettes', error);
  }

  return [];
};

const fetchBrandKitColorPalettesFromServer = async (): Promise<UserColorPaletteInfo[]> => {
  try {
    return (await window.PMW.readLocal('colorpalettes/getBrandKitColorPalettes', {})) as UserColorPaletteInfo[];
  } catch (error) {
    console.error('Error fetching brand kit color palettes', error);
  }

  return [];
};

const fetchColorPaletteFilterPillFromServer = async (): Promise<ColorPalettePill[]> => {
  try {
    return (await window.PMW.readLocal('colorpalettes/getColorPalettePillsToShow', {})) as ColorPalettePill[];
  } catch (error) {
    console.error('Error fetching color palette pills', error);
  }

  return [];
};

const fetchRecentPalettesFromServer = async (): Promise<ColorPaletteInfo[]> => {
  try {
    return (await window.PMW.readLocal('colorpalettes/getRecentPalettes', {})) as ColorPaletteInfo[];
  } catch (error) {
    console.error('Error fetching recent palettes', error);
  }

  return [];
};
